// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppPlatform, selectAppStoreType } from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import { CommonSetupFlowLayout } from '../sharedComponents/CommonSetupFlowContainer';
import { NavigationBar } from '../sharedComponents/NavigationBar';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useQuickStartCISteps } from './hooks/useQuickStartCISteps';
import Icon, { ToolFilled } from '@ant-design/icons';
import Progress from 'antd/lib/progress';
import Typography from 'antd/lib/typography';
import { createAppRoute } from '../../helpers/routeHelpers';
import { useHistory } from 'react-router-dom';
import { ComponentType } from 'react';
import { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';

const ProgressViewComponent: React.FC<{ stepsCount: number; currentStep: number; color?: string }> = ({
    stepsCount,
    currentStep,
    color,
}) => {
    return (
        <div
            style={{
                padding: 20,
            }}
        >
            <Progress
                style={{
                    width: '100%',
                }}
                className="quick-start-ci-progress"
                percent={(currentStep / stepsCount) * 100}
                steps={stepsCount}
                showInfo={false}
                strokeColor={color ?? antGrey.grey8}
            />
        </div>
    );
};

const SubHeader: React.FC<
    React.PropsWithChildren<{
        icon: ComponentType<CustomIconComponentProps>;
        title: string;
        iconColor?: string;
    }>
> = ({ icon, title, children, iconColor }) => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'start',
            }}
        >
            <Icon component={icon} style={{ color: iconColor ?? antGrey.grey4, fontSize: 60, marginRight: 30 }} />
            <div
                style={{
                    flex: 1,
                }}
            >
                <Typography.Text
                    style={{ color: antGrey.grey9, fontSize: 24, fontWeight: 600, paddingBottom: 36, display: 'block' }}
                >
                    {title}
                </Typography.Text>
                {children}
            </div>
        </div>
    );
};

interface QuickStartCIContainerProps extends OrgComponentProps {
    appId?: string;
}

export const QuickStartCIContainer: React.FC<QuickStartCIContainerProps> = (props) => {
    const { appId, orgId } = props;

    const { currentSetupStep, goToNextStep, goToPreviousStep, hasNextStep, hasPreviousStep, currentStep, stepCount } =
        useQuickStartCISteps(appId, orgId);
    const { icon, SetupViewComponent, InfoViewComponent, headerTitle, isCompleteSelector } = currentSetupStep ?? {};

    const canContinue = useSelector((state: RootState) => isCompleteSelector(state, { appId, orgId }));

    const navIcon = icon(null);

    const platform = useSelector((state: RootState) => (appId ? selectAppPlatform(state, appId) : null));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const navHeaderTitle = typeof headerTitle === 'function' ? headerTitle(platform, appStoreType) : headerTitle;
    const history = useHistory();

    return (
        <CommonSetupFlowLayout
            navigationBar={
                <NavigationBar
                    style={{ backgroundColor: antGrey.grey8 }}
                    title={`Quickstart CI/CD`}
                    description={`Spin up a basic pipeline for your mobile app in minutes.`}
                    icon={ToolFilled}
                    canContinue={canContinue}
                    isFinalStep={!hasNextStep}
                    canGoBack={hasPreviousStep}
                    onContinue={goToNextStep}
                    onGoBack={goToPreviousStep}
                    shouldAskForConfirmation={false}
                    onClose={() => {
                        const route = appId ? createAppRoute(orgId, appId) : '/';
                        history.push(route);
                    }}
                />
            }
            sideBar={InfoViewComponent && <InfoViewComponent {...props} />}
            footer={
                <ProgressViewComponent
                    currentStep={currentStep}
                    stepsCount={stepCount}
                    color={hasNextStep ? null : runwayColors.statusGreen}
                />
            }
        >
            <SubHeader icon={navIcon} iconColor={hasNextStep ? null : runwayColors.statusGreen} title={navHeaderTitle}>
                {SetupViewComponent && <SetupViewComponent {...props} />}
            </SubHeader>
        </CommonSetupFlowLayout>
    );
};
